import * as React from 'react';
import * as PropTypes from 'prop-types';
import {
  SelectionZone,
  ISelectionZoneProps,
  MarqueeSelection,
  ISelection,
  SelectionMode
} from 'office-ui-fabric-react/lib/MarqueeSelection';
import {
  autobind
} from 'office-ui-fabric-react/lib/Utilities';
import {
  TilesList,
  ITilesListProps,
  ITilesGridSegment,
  ITilesGridItem,
  ITileSize,
  TilesGridMode
} from '@uifabric/experiments/lib/TilesList';
import { ITileProps } from '@uifabric/experiments/lib/Tile';
import { provideContext, connectToContext } from '../../utilities/components/Context';
import { connectToItemCache } from '../decorators/connectToItemCache';
import { IItemCache } from '../../models/IItemCacheObserver';
import { ItemTile, IItemTileProps } from '../itemTile/ItemTile';
import {
  withLayoutRenderComplete, ILayoutRenderProps, IDecorateWithLayoutRenderCompleteProps
} from '../decorators/withLayoutRenderComplete';
import { ItemPage } from '../itemPage/ItemPage';
import { WithInvokeAction } from '../withInvokeAction/WithInvokeAction';
import * as ItemSetTilesListStyles from './ItemSetTilesList.scss';
import { childItemsControl } from '../../controls/childItemsControl';
import { ShimmerTile } from '@uifabric/experiments/lib/components/Shimmer/ShimmerTile/ShimmerTile';
import { ItemDefaultDropZone } from '../defaultDropZone/DefaultDropZone';
import { IActionDropZoneStateProps } from '../decorators/asActionDropZone';
import { childItemsFacet, pageFacet } from '../../facets/SetFacets';
import { isGroupFacet } from '../../facets/GroupFacets';
import { errorFacet } from '../../facets/DemandFacets';
import { displayNameFacet } from '../../facets/ItemFacets';
import { IActionStateProps } from '../withAction/WithAction';
import { IDecoratedProps } from '../../utilities/components/Decorators';
import { withResponsiveMode } from '../decorators/withResponsiveMode';
import { ResponsiveMode } from 'office-ui-fabric-react/lib/utilities/decorators/withResponsiveMode';

const SIZES: {
  [P in TilesListSize]: {
    tileDefaultHeight: number;
    tileDefaultWidth: number;
    documentGroupTileMargin: number;
    photoGroupTileMargin: number;
    groupMargin: number;
  };
} = {
    small: {
      tileDefaultHeight: 135,
      tileDefaultWidth: 138,
      documentGroupTileMargin: 12,
      photoGroupTileMargin: 8,
      groupMargin: 40
    },
    large: {
      tileDefaultHeight: 171,
      tileDefaultWidth: 176,
      documentGroupTileMargin: 8,
      photoGroupTileMargin: 8,
      groupMargin: 40
    }
  };

export type IItemTileCellProps = IDecoratedProps<IItemTileProps, {}, {}>;

const SelectionContext = provideContext({
  selection: PropTypes.object
}, (props: {
  selection: ISelection;
}) => {
    return {
      selection: props.selection
    };
  });

const SelectableItemTile = connectToContext({
  selection: PropTypes.object
}, (context: {
  selection: ISelection;
}): Pick<ITileProps, 'selection'> => {
    return {
      selection: context.selection
    };
  })(ItemTile);

const ItemTileCell = (props: IItemTileCellProps): JSX.Element => {
  return (
    // Render the action used to open the item, so that it bubbles up its availability and execute function
    // to the action dispatcher.
    <WithInvokeAction itemKey={ props.itemKey }>{ ({ isAvailable }: IActionStateProps) => {
      return (
        <ItemDefaultDropZone itemKey={ props.itemKey } layoutMode='absolute'>{ ({ isDropping }: IActionDropZoneStateProps) => (
          <SelectableItemTile
            contentSize={ props.contentSize }
            itemKey={ props.itemKey }
            tileSize={ props.tileSize }
            selectionIndex={ props.selectionIndex }
            invokeSelection={ isAvailable }
            href={ isAvailable ? '#' : undefined }
            { ...props }
          />
        ) }</ItemDefaultDropZone>
      );
    } }</WithInvokeAction>
  );
};

/**
 * @public
 */
export interface IItemSetTilesListCellContent {
  key: string;
  index: number;
  count: number;
  tileSize: ITileProps['tileSize'];
  renderer?: React.ComponentType<IItemTileCellProps>;
}

type TilesListSize = 'small' | 'large';

/**
 * @public
 */
export interface IDefaultTilesListProps extends ITilesListProps<IItemSetTilesListCellContent> {
  selection?: ISelection;
  selectionItems: {
    key: string;
  }[];
  selectionMode?: SelectionMode;
  onItemInvoked?: ISelectionZoneProps['onItemInvoked'];
  onItemContextMenu?: ISelectionZoneProps['onItemContextMenu'];
  setKey: string;
  disableMarqueeSelection?: boolean;
  ariaLabel?: string;
}

declare class ItemTilesListClass extends TilesList<IItemSetTilesListCellContent> { }

const ItemTilesList: typeof ItemTilesListClass = TilesList;

class DefaultTilesList extends React.PureComponent<IDefaultTilesListProps, {}> {
  constructor(props: IDefaultTilesListProps) {
    super(props);

    const {
      selection,
      selectionItems
    } = this.props;

    if (selection) {
      selection.setItems(selectionItems, true);
    }
  }

  public render(): JSX.Element {
    const {
      selection,
      selectionMode,
      ref,
      disableMarqueeSelection,
      ariaLabel,
      ...tilesListProps
    } = this.props;

    const tilesList = (
      <ItemTilesList
        role='list'
        className={ ItemSetTilesListStyles.root }
        aria-label={ ariaLabel }
        { ...tilesListProps }
      />
    );

    const tilesListWithSelection = (
      <SelectionZone
        selection={ selection! }
        selectionMode={ selectionMode }
        onItemInvoked={ this.props.onItemInvoked }
        onItemContextMenu={ this.props.onItemContextMenu }
        disableAutoSelectOnInputElements={ true }
      >
        <SelectionContext selection={ selection! }>
          { tilesList }
        </SelectionContext>
      </SelectionZone>
    );

    return selection ?
      (
        disableMarqueeSelection ?
          (
            tilesListWithSelection
          ) :
          (
            <MarqueeSelection
              selection={ selection }
              onShouldStartSelection={ this._onShouldStartSelection }
            >
              { tilesListWithSelection }
            </MarqueeSelection>
          )
      ) : (
        tilesList
      );
  }

  public componentWillReceiveProps(nextProps: IDefaultTilesListProps): void {
    const {
      selection,
      selectionItems
    } = nextProps;

    if (selection) {
      selection.setItems(selectionItems, nextProps.setKey !== this.props.setKey || !nextProps.setKey);
    }
  }

  @autobind
  private _onShouldStartSelection(event: MouseEvent): boolean {
    let currentElement: HTMLElement | null = event.target as HTMLElement;

    while (currentElement && currentElement !== document.body) {
      const selectionIndex = currentElement.getAttribute('data-selection-index');

      if (selectionIndex !== null) {
        return false;
      }

      currentElement = currentElement.parentElement;
    }

    return true;
  }
}

const NormalizedTilesList = withLayoutRenderComplete()(DefaultTilesList);

/**
 * @public
 */
export type IItemSetTilesListProps = IDecoratedProps<IDecorateWithLayoutRenderCompleteProps<IDefaultTilesListProps>,
  Pick<IDefaultTilesListProps, 'items' | 'selectionItems' | 'setKey'> &
  Pick<ILayoutRenderProps, 'isDataReadyForLayout'>,
  {
    itemKey?: string | undefined;
    doNotRenderShimmerTile?: boolean | undefined;
    responsiveMode?: ResponsiveMode | undefined;
  }>;

/**
 * @public
 */
export const ItemSetTilesList: React.ComponentType<IItemSetTilesListProps> =
  withResponsiveMode()(connectToItemCache((itemCache: IItemCache, props: {
    itemKey?: string | undefined;
    doNotRenderShimmerTile?: boolean | undefined;
    responsiveMode?: ResponsiveMode | undefined;
  }): Pick<IDefaultTilesListProps, 'items' | 'selectionItems' | 'setKey'> &
    Pick<ILayoutRenderProps, 'isDataReadyForLayout'> => {
    const childItems = itemCache.demandItemFacet(childItemsFacet, props.itemKey);

    const childItemsControlHandler = itemCache.demandItemFacet(childItemsControl, props.itemKey);

    const TileRenderer = ItemTileCell;

    const {
      itemKeys
    } = childItemsControl.evaluate(childItemsControlHandler)(itemCache, {
      itemKey: props.itemKey,
      itemKeys: childItems && childItems.itemKeys
    });

    const items: (ITilesGridItem<IItemSetTilesListCellContent> | ITilesGridSegment<IItemSetTilesListCellContent>)[] = [];

    const selectionItems: {
      key: string;
    }[] = [];

    const defaultGridItems: ITilesGridItem<IItemSetTilesListCellContent>[] = [];
    const tileListSize: ITileProps['tileSize'] = props.responsiveMode === ResponsiveMode.large ? 'large' : 'small';

    let index = 0;
    const sizes = SIZES[tileListSize];

    if (itemKeys) {
      for (let i = 0, length = itemKeys.length; i < length; i++) {
        const childItemKey = itemKeys[i];

        let groupChildItems: (typeof childItemsFacet.type) | undefined;

        if (itemCache.isItemFacetResolved(pageFacet, childItemKey)) {
          items.push({
            key: childItemKey,
            content: {
              key: childItemKey,
              count: length, // TODO this needs to be the total number of items.
              index: -1,
              tileSize: tileListSize
            },
            onRender: renderPage
          });
        } else if (itemCache.isItemFacetResolved(childItemsFacet, childItemKey) &&
          (groupChildItems = itemCache.demandItemFacet(childItemsFacet, childItemKey))) {
          items.push({
            key: childItemKey,
            content: {
              key: childItemKey,
              count: length, // TODO this needs to be the total number of items.
              index: -1,
              tileSize: tileListSize
            },
            onRender: renderHeader
          });

          const groupItems: ITilesGridItem<IItemSetTilesListCellContent>[] = [];
          let groupChildItemKeys: string[] = [];

          if (itemCache.isItemFacetResolved(isGroupFacet, groupChildItems.itemKeys[0])) {
            for (const nestedGroupKey of groupChildItems.itemKeys) {
              const nestedGroupChildren = itemCache.demandItemFacet(childItemsFacet, nestedGroupKey);
              if (nestedGroupChildren) {
                groupChildItemKeys = groupChildItemKeys.concat(nestedGroupChildren.itemKeys);
              }
            }
          } else {
            groupChildItemKeys = groupChildItems.itemKeys;
          }

          for (const groupChildItemKey of groupChildItemKeys) {
            selectionItems.push({
              key: groupChildItemKey
            });

            groupItems.push({
              key: groupChildItemKey,
              content: {
                key: groupChildItemKey,
                count: groupChildItems.itemKeys.length,
                index: index,
                tileSize: tileListSize,
                renderer: TileRenderer
              },
              desiredSize: getDesiredSize(tileListSize),
              onRender: renderItemTile
            });

            index++;
          }

          const groupSegment: ITilesGridSegment<IItemSetTilesListCellContent> = {
            key: childItemKey,
            items: groupItems,
            mode: tileListSize === 'large' ?
              TilesGridMode.stack :
              TilesGridMode.fillHorizontal,
            marginBottom: sizes.groupMargin,
            spacing: 12,
            minRowHeight: sizes.tileDefaultHeight
          };

          items.push(groupSegment);
        } else {
          selectionItems.push({
            key: childItemKey
          });

          defaultGridItems.push({
            key: childItemKey,
            content: {
              key: childItemKey,
              count: length, // TODO this needs to be the total number of items.
              index: index,
              tileSize: tileListSize,
              renderer: TileRenderer
            },
            desiredSize: getDesiredSize(tileListSize),
            onRender: renderItemTile
          });

          index++;
        }
      }

      if (defaultGridItems.length > 0) {
        items.push({
          key: '',
          items: defaultGridItems,
          mode: TilesGridMode.stack,
          marginBottom: sizes.groupMargin,
          spacing: 12,
          minRowHeight: sizes.tileDefaultHeight
        });
      }
    } else if (!props.doNotRenderShimmerTile) {
      items.push({
        key: '',
        items: [{
          key: '',
          content: {
            key: '',
            count: length,
            index: index,
            tileSize: tileListSize
          },
          desiredSize: getDesiredSize(tileListSize),
          onRender: renderShimmerTile,
          isPlaceholder: true
        }],
        mode: TilesGridMode.stack,
        marginBottom: sizes.groupMargin,
        spacing: 12,
        minRowHeight: sizes.tileDefaultHeight,
        isPlaceholder: true
      });
    }

    const isDataReadyForLayout =
      itemCache.isItemFacetResolved(childItemsFacet, props.itemKey) ||
      itemCache.isItemFacetResolved(errorFacet, props.itemKey);

    return {
      setKey: props.itemKey || '',
      items: items,
      selectionItems: selectionItems,
      isDataReadyForLayout: isDataReadyForLayout
    };
  })(NormalizedTilesList));

function renderItemTile(content: IItemSetTilesListCellContent, finalSize: ITileSize | undefined): JSX.Element | null {
  const {
    renderer: Renderer
  } = content;

  return Renderer ? (
    <Renderer
      contentSize={ finalSize }
      itemKey={ content.key }
      tileSize={ content.tileSize }
      selectionIndex={ content.index }
    />
  ) : null;
}

const Header = connectToItemCache((itemCache: IItemCache, props: {
  itemKey: string;
}) => {
  const displayName = itemCache.demandItemFacet(displayNameFacet, props.itemKey);

  return {
    text: displayName || ''
  };
})((props: {
  text: string;
}) => {
  return (
    <div>
      <h3>{ props.text }</h3>
    </div>
  );
});

function renderHeader(content: IItemSetTilesListCellContent): JSX.Element {
  return (
    <Header itemKey={ content.key } />
  );
}

function renderPage(content: IItemSetTilesListCellContent): JSX.Element {
  return (
    <ItemPage
      itemKey={ content.key }
    />
  );
}

function renderShimmerTile(content: IItemSetTilesListCellContent, finalSize: ITileSize | undefined): JSX.Element {
  return (
    <ShimmerTile
      contentSize={ finalSize }
      itemName={ true }
      itemActivity={ true }
      itemThumbnail={ true }
    />
  );
}

function getDesiredSize(tilesListSize: TilesListSize): ITileSize {
  const sizes = SIZES[tilesListSize];

  return {
    width: sizes.tileDefaultWidth,
    height: sizes.tileDefaultHeight
  };
}
